/** "Someone is waiting for you" — on every screen, not just the two that
 *  happened to have the presence board.
 *
 *  Requested 2026-10-01: an operator reading a report, editing a
 *  campaign, or anywhere else in the app had no way to know a
 *  beneficiary had joined their call. The board that says so lived on
 *  the dashboard and the campaign overview, which are precisely the two
 *  screens you are NOT on while doing the work.
 *
 *  Two urgencies, and keeping them apart is the point:
 *
 *    WAITING   a beneficiary is in the room now. Someone is sitting in
 *              an empty call losing patience. Not dismissible — a
 *              dismiss button here invites exactly the mistake the
 *              banner exists to prevent, and it clears itself the moment
 *              the operator joins or the beneficiary leaves.
 *
 *    UPCOMING  scheduled within the lead window. Nobody is waiting yet;
 *              the point is not being late. Dismissible, because an
 *              operator who has seen it and is deliberately finishing
 *              something else should not be nagged — and it comes back
 *              on its own if the beneficiary then joins, because that is
 *              a different fact.
 *
 *  Waiting always outranks upcoming. Showing both at once would bury the
 *  one that matters under the one that doesn't yet.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import { useHasPermission } from "@/lib/permissions";
import { playSound } from "@/lib/soundEffects";
import type { LiveResponse, LiveSession, UpcomingSession } from "./liveSessions";
import { LIVE_QUERY_KEY, minutesUntil } from "./liveSessions";

export function CallAlertBanner() {
  const { t } = useTranslation();
  const canSee = useHasPermission("session.read");
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: LIVE_QUERY_KEY,
    queryFn: async () => (await api.get<LiveResponse>(ApiPaths.sessionsLive)).data,
    refetchInterval: 15000,
    enabled: canSee,
  });

  const waiting: LiveSession[] = q.data?.sessions ?? [];
  const upcoming: UpcomingSession[] = (q.data?.upcoming ?? []).filter(
    (s) => !dismissed.has(s.id),
  );

  useAlertOnArrival(waiting, canSee, q.isSuccess);

  if (!canSee) return null;

  if (waiting.length > 0) {
    const first = waiting[0]!;
    const name = first.beneficiary_name || t("sessions.live.aBeneficiary");
    return (
      <Banner
        tone="waiting"
        testId="call-alert-waiting"
        title={
          waiting.length > 1
            ? t("sessions.alert.waitingMany", { count: waiting.length })
            : t("sessions.alert.waitingOne", { name })
        }
        detail={
          waiting.length > 1
            ? waiting
                .map((s) => s.beneficiary_name || t("sessions.live.aBeneficiary"))
                .join(" · ")
            : first.campaign_name || first.title
        }
        to={`/sessions/${first.id}`}
        cta={t("sessions.alert.joinNow")}
      />
    );
  }

  if (upcoming.length > 0) {
    const next = upcoming[0]!;
    const name = next.beneficiary_name || t("sessions.live.aBeneficiary");
    const mins = minutesUntil(next.scheduled_for);
    return (
      <Banner
        tone="upcoming"
        testId="call-alert-upcoming"
        title={
          next.overdue
            ? t("sessions.alert.overdue", { name })
            : mins <= 0
              ? t("sessions.alert.startingNow", { name })
              : t("sessions.alert.inMinutes", { name, count: mins })
        }
        detail={next.campaign_name || next.title}
        to={`/sessions/${next.id}`}
        cta={t("sessions.alert.join")}
        onDismiss={() =>
          setDismissed((prev) => new Set(prev).add(next.id))
        }
        dismissLabel={t("sessions.alert.dismiss")}
      />
    );
  }

  return null;
}

/**
 * Sound + browser notification when a beneficiary ARRIVES.
 *
 * Only on arrival: re-alerting on every 15s poll for someone already
 * waiting would train the operator to ignore the sound, which costs more
 * than it buys.
 *
 * `loaded` is what makes the priming honest, and it is the part the
 * original version on LiveInspectionsBoard got wrong. Priming on the
 * first render primes against an EMPTY list, because the query has not
 * answered yet — so the first real payload looks like a room full of
 * new arrivals and a simple page refresh during a call sounded the
 * alarm. Prime on the first LOADED render instead.
 */
function useAlertOnArrival(
  waiting: LiveSession[],
  enabled: boolean,
  loaded: boolean,
) {
  const { t } = useTranslation();
  const seen = useRef<Set<string>>(new Set());
  const primed = useRef(false);

  useEffect(() => {
    if (
      enabled &&
      typeof Notification !== "undefined" &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !loaded) return;
    if (!primed.current) {
      waiting.forEach((s) => seen.current.add(s.id));
      primed.current = true;
      return;
    }
    const fresh = waiting.filter((s) => !seen.current.has(s.id));
    // Forget whoever has left, so a beneficiary who drops and rejoins
    // alerts again — that is a new arrival from the operator's side.
    seen.current = new Set(waiting.map((s) => s.id));
    if (fresh.length === 0) return;

    playSound("notify").catch(() => {});
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      fresh.forEach((s) => {
        try {
          new Notification(t("sessions.live.pushTitle"), {
            body: t("sessions.live.pushBody", {
              name: s.beneficiary_name || t("sessions.live.aBeneficiary"),
            }),
            tag: `nxb-waiting-${s.id}`,
          });
        } catch {
          /* notifications unavailable */
        }
      });
    }
  }, [waiting, enabled, loaded, t]);
}

function Banner({
  tone,
  title,
  detail,
  to,
  cta,
  testId,
  onDismiss,
  dismissLabel,
}: {
  tone: "waiting" | "upcoming";
  title: string;
  detail?: string;
  to: string;
  cta: string;
  testId: string;
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  const waiting = tone === "waiting";
  return (
    <div
      data-testid={testId}
      role={waiting ? "alert" : "status"}
      aria-live={waiting ? "assertive" : "polite"}
      // `motion-safe:` so the pulse stops entirely for anyone who has
      // asked their OS for reduced motion. A banner that cannot be
      // switched off AND moves is the combination that makes a product
      // unusable for some people; the colour and the wording still say
      // everything the animation says.
      className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${
        waiting
          ? "border-nb-mint/50 bg-nb-mint/10 motion-safe:animate-alert-pulse"
          : "border-nb-warn/40 bg-nb-warn/10"
      }`}
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {waiting && (
          <>
            {/* Two rings, the second delayed, so the dot reads as a
                repeating blip rather than one slow throb. Both are
                motion-safe — the solid dot underneath carries the
                meaning on its own. */}
            <span className="absolute inline-flex h-full w-full rounded-full bg-nb-mint opacity-75 motion-safe:animate-ping" />
            <span
              className="absolute inline-flex h-full w-full rounded-full bg-nb-mint opacity-50 motion-safe:animate-ping"
              style={{ animationDelay: "0.55s" }}
            />
          </>
        )}
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
            waiting ? "bg-nb-mint" : "bg-nb-warn"
          }`}
        />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-nb-text">{title}</p>
        {detail && (
          <p className="truncate text-xs text-nb-text-2">{detail}</p>
        )}
      </div>
      <Link
        to={to}
        className={waiting ? "btn-primary text-xs" : "btn-ghost text-xs"}
        data-testid={`${testId}-cta`}
      >
        {cta}
      </Link>
      {onDismiss && (
        <button
          type="button"
          className="text-xs text-nb-text-3 hover:text-nb-text-2"
          onClick={onDismiss}
          data-testid={`${testId}-dismiss`}
        >
          {dismissLabel}
        </button>
      )}
    </div>
  );
}
